import _ from 'lodash';

import { MapConstants } from '../../MapConstants';
import { IOpening } from '../../Models/IOpening';
import { IRoom } from '../../Models/IRoom';
import { Editor } from '../Editor';
import { isObjectsEquals } from '../Funcs';
import { PointUtils } from '../PointUtils';
import { CoordPoint, Equation, EquationOpening, LineCoordPoint, Wall, WallLine } from '../Types';
import { qSVG } from '../qSVG';
import { WallTools } from './WallTools';
import { WallUtils } from './WallUtils';
import { RoomUtils } from '../Room/RoomUtils';
import { ObjectUtils } from 'Helpers';

type WallMoveParams = {
    binder: Wall;
    walls: Array<Wall>;
    rooms: Array<IRoom>;
    openings: Array<IOpening>;
    equationFollowers?: Array<any>;

    equation1: Equation;
    equation2: Equation;
    equation3: Equation;
    equationsObj: Array<EquationOpening>;

    simulation?: boolean;
};

export type WallMoveResult = {
    walls: Array<Wall>;
    rooms: Array<IRoom>;
    openings: Array<IOpening>;
    wallMoveSuccess?: boolean;
};
export class WallMove {
    public static prepareWallMove = (
        targetWall: Wall,
        binder: Wall,
        newSize: number,
        openings: Array<IOpening>,
        rooms: Array<IRoom>,
        walls: Array<Wall>,
        simulation?: boolean
    ): WallMoveResult => {
        const meter = MapConstants.meter;
        let moveSize = (newSize * meter) / 100 - WallUtils.wallSize(targetWall); //relative new size
        const binderParent = WallUtils.getWallParent(binder, walls)!;
        const binderChild = WallUtils.getWallChild(binder, walls)!;

        const newWallCoords = this.getNewWallCoordinates(binder, moveSize); //1 meter
        if (binder.sideName === 'right_crown') {
            // exception for diagonal wall (e.g. table)
            let tableEquation = Editor.createEquationFromWall(binderParent);
            let newRightCrownEquation = Editor.createEquationFromWall(newWallCoords);
            newWallCoords.start = qSVG.intersectionOfEquations(
                tableEquation,
                newRightCrownEquation,
                'obj'
            ) as CoordPoint;
            newWallCoords.angleRad = qSVG.getAngle(newWallCoords.start, newWallCoords.end).rad;
        } else if (binder.sideName === 'left_crown') {
            let tableEquation = Editor.createEquationFromWall(binderChild);
            let newLeftCrownEquation = Editor.createEquationFromWall(newWallCoords);
            newWallCoords.end = qSVG.intersectionOfEquations(tableEquation, newLeftCrownEquation, 'obj') as CoordPoint;
            newWallCoords.angleRad = qSVG.getAngle(newWallCoords.start, newWallCoords.end).rad;
        }

        const equation1 = qSVG.createEquation(
            binderParent.start.x,
            binderParent.start.y,
            newWallCoords.start.x,
            newWallCoords.start.y
        );
        const equation2 = qSVG.createEquation(
            newWallCoords.start.x,
            newWallCoords.start.y,
            newWallCoords.end.x,
            newWallCoords.end.y
        );
        //!NOT USED
        const equation3 = qSVG.createEquation(
            newWallCoords.end.x,
            newWallCoords.end.y,
            binderChild.end.x,
            binderChild.end.y
        );

        const equationFollowers: Array<any> = [];

        let equationsObj: Array<EquationOpening> = [];
        const wallOpenings = Editor.objFromWall(binder, openings); // LIST Opening ON EDGE
        wallOpenings.forEach((opening) => {
            equationsObj.push({
                obj: opening,
                wall: binder,
                eq: qSVG.perpendicularEquation(equation2, opening.x!, opening.y!)!,
            });
        });
        const result = this.wallMove({
            binder,
            rooms,
            openings,
            walls,
            equationFollowers,
            equationsObj,
            equation1,
            equation2,
            equation3,
            simulation,
        });
        return result;
    };

    public static getNewWallCoordinates(wall: Wall, moveSize: number): WallLine {
        let directionAngle = this.Add90CounterClockwise(wall.angleRad);
        const start = {
            x: wall.start.x + _.round(Math.cos(directionAngle) * moveSize, 5),
            y: wall.start.y + _.round(Math.sin(directionAngle) * moveSize, 5),
        };
        const end = {
            x: wall.end.x + _.round(Math.cos(directionAngle) * moveSize, 5),
            y: wall.end.y + _.round(Math.sin(directionAngle) * moveSize, 5),
        };

        return {
            start,
            end,
            angleRad: qSVG.getAngle(start, end).rad,
        };
    }

    public static Add90CounterClockwise(angle: number) {
        switch (angle) {
            case 0:
                return -Math.PI / 2;
            case Math.PI / 2:
                return 0;
            case Math.PI:
                return Math.PI / 2;
            case -Math.PI / 2:
                return Math.PI;
            default:
                return 0;
        }
    }

    public static Add90Clockwise = (angle: number) => {
        switch (angle) {
            case 0:
                return Math.PI / 2;
            case Math.PI / 2:
                return Math.PI;
            case Math.PI:
                return -Math.PI / 2;
            case -Math.PI / 2:
                return 0;
            default:
                return 0;
        }
    };

    public static checkIfRightAngled(angle: number) {
        return angle === 0 || angle === -90 || angle === 90 || angle === 180;
    }

    public static equalWithTolerance(valueA: number, valueB: number, tolerance: number) {
        return valueA > valueB - tolerance && valueA < valueB + tolerance;
    }

    // On prend la différence entre la nouvelle équation2 et l'équation2 actuelle, on execute wallMove tous les 10cm
    // Cela évite les chevauchement avec des mouvements de souris vif
    public static wallMoveSmooth(params: WallMoveParams): WallMoveResult {
        const { binder, walls, equation1: previousEquation1, equation2, equation3: previousEquation3 } = params;
        const binderParent = WallUtils.getWallParent(binder, walls)!;
        const binderChild = WallUtils.getWallChild(binder, walls)!;

        const equation1 = Editor.createEquationFromWall(binderParent, previousEquation1);
        const previous_equation2 = Editor.createEquationFromWall(binder);
        const equation3 = Editor.createEquationFromWall(binderChild, previousEquation3);

        const ten_cm = MapConstants.meter * 0.1;
        const difference = equation2.B - previous_equation2.B;
        const sectionModulo = difference % ten_cm;
        const differenceWithoutModulo = difference - sectionModulo;
        const number_of_sections = Math.abs(differenceWithoutModulo !== 0 ? differenceWithoutModulo / ten_cm : 0);
        const iterator = differenceWithoutModulo / number_of_sections;

        let moveResult: WallMoveResult = { walls: params.walls, rooms: params.rooms, openings: params.openings };
        equation2.B -= differenceWithoutModulo;
        moveResult = this.wallMove({ ...params, equation1, equation3 });
        for (let i = 0; i < number_of_sections; i++) {
            equation2.B += iterator;
            moveResult = this.wallMove({ ...params, equation1, equation3 });
            if (!moveResult.wallMoveSuccess) {
                break;
            }
        }
        for (const room of moveResult.rooms) {
            room.coords = RoomUtils.getCoordsFromWalls(room);
        }

        return moveResult;
    }

    public static wallMove(params: WallMoveParams): WallMoveResult {
        let {
            binder,
            walls,
            rooms,
            openings,
            equationFollowers = [],
            equation1,
            equation2,
            equation3,
            equationsObj,
            simulation = false,
        } = params;
        let intersection1 = qSVG.intersectionOfEquations(equation1, equation2, 'obj') as CoordPoint;
        let intersection2 = qSVG.intersectionOfEquations(equation2, equation3, 'obj') as CoordPoint;
        const binderParent = WallUtils.getWallParent(binder, walls)!;
        const binderChild = WallUtils.getWallChild(binder, walls)!;

        if (binderParent) {
            if (isObjectsEquals(binderParent.end, binder.start)) {
                binderParent.end = intersection1;
            } else if (isObjectsEquals(binderParent.start, binder.start)) {
                binderParent.start = intersection1;
            } else {
                binderParent.end = intersection1;
            }
        }

        if (binderChild) {
            if (isObjectsEquals(binderChild.start, binder.end)) {
                binderChild.start = intersection2;
            } else if (isObjectsEquals(binderChild.end, binder.end)) {
                binderChild.end = intersection2;
            } else {
                binderChild.start = intersection2;
            }
        }

        binder.start = intersection1;
        binder.end = intersection2;

        // THE EQ FOLLOWED BY eq (PARENT EQ1 --- CHILD EQ3)
        if (equation1.follow) {
            if (!qSVG.rayCasting(intersection1, equation1.backUp!.coords)) {
                // IF OUT OF WALL FOLLOWED
                var distanceFromStart = qSVG.gap(equation1.backUp!.start, intersection1);
                var distanceFromEnd = qSVG.gap(equation1.backUp!.end, intersection1);
                if (distanceFromStart > distanceFromEnd) {
                    // NEAR FROM End
                    equation1.follow.end = intersection1;
                } else {
                    equation1.follow.start = intersection1;
                }
            } else {
                equation1.follow.end = equation1.backUp!.end;
                equation1.follow.start = equation1.backUp!.start;
            }
        }
        if (equation3.follow) {
            if (!qSVG.rayCasting(intersection2, equation3.backUp!.coords)) {
                // IF OUT OF WALL FOLLOWED
                const distanceFromStart = qSVG.gap(equation3.backUp!.start, intersection2);
                const distanceFromEnd = qSVG.gap(equation3.backUp!.end, intersection2);
                if (distanceFromStart > distanceFromEnd) {
                    // NEAR FROM End
                    equation3.follow.end = intersection2;
                } else {
                    equation3.follow.start = intersection2;
                }
            } else {
                equation3.follow.end = equation3.backUp!.end;
                equation3.follow.start = equation3.backUp!.start;
            }
        }

        // equationFollowers.forEach((equationFollower, i) => {
        //     const intersectionFollower = qSVG.intersectionOfEquations(
        //         equationFollower.eq,
        //         equation2,
        //         'obj'
        //     ) as CoordPoint;
        //     if (
        //         qSVG.btwn(intersectionFollower.x, binder.start.x, binder.end.x, 'round') &&
        //         qSVG.btwn(intersectionFollower.y, binder.start.y, binder.end.y, 'round')
        //     ) {
        //         const size = qSVG.measure(equationFollower.wall.start, equationFollower.wall.end);
        //         if (equationFollower.type === 'start') {
        //             equationFollower.wall.start = intersectionFollower;
        //             if (size < 5) {
        //                 if (!equationFollower.wall.child) {
        //                     walls.splice(walls.indexOf(equationFollower.wall), 1);
        //                     equationFollowers.splice(i, 1);
        //                 }
        //             }
        //         }
        //         if (equationFollower.type === 'end') {
        //             equationFollower.wall.end = intersectionFollower;
        //             if (size < 5) {
        //                 if (!equationFollower.wall.parent) {
        //                     walls.splice(walls.indexOf(equationFollower.wall), 1);
        //                     equationFollowers.splice(i, 1);
        //                 }
        //             }
        //         }
        //     }
        // });

        if (!simulation) {
            Editor.wallsComputing(walls);
            const wallPolygons = qSVG.polygonize(walls);
            Editor.setRoomCoordsFromWall(wallPolygons, rooms);

            // OBJDATA(s) FOLLOW 90° EDGE SELECTED
            equationsObj.forEach((equationObj) => {
                const objTarget = equationObj.obj;
                equation2 = Editor.createEquationFromWall(binder);
                equationObj.eq = qSVG.perpendicularEquation(equation2, objTarget.x!, objTarget.y!)!;
                const intersectionObj = qSVG.intersectionOfEquations(equationObj.eq, equation2) as Array<number>;
                // NEW COORDS OBJDATA[o]
                objTarget.x = intersectionObj[0];
                objTarget.y = intersectionObj[1];
                const limits = PointUtils.limitObj(equation2, objTarget.size!, objTarget as CoordPoint);

                //SI objtaget n'est pas sur un mur
                //chercher l'intersection le plus proche

                //ou faire des simulations

                if (!qSVG.btwn(limits[0].x, binder.start.x, binder.end.x, false, MapConstants.tolerance_round_value)) {
                    if (
                        qSVG.btwn(binder.start.x, limits[0].x, limits[1].x, false, MapConstants.tolerance_round_value)
                    ) {
                        //take start wall point as reference
                        const measure = qSVG.measure(
                            { x: objTarget.x! - objTarget.size! / 2, y: objTarget.y! },
                            binder.start
                        );
                        objTarget.x! += measure;
                    } else if (
                        qSVG.btwn(binder.end.x, limits[0].x, limits[1].x, false, MapConstants.tolerance_round_value)
                    ) {
                        //take end wall point as reference
                        const measure = qSVG.measure(limits[0], binder.end);
                        objTarget.x! -= measure;
                    }
                }
                if (!qSVG.btwn(limits[1].x, binder.start.x, binder.end.x, false, MapConstants.tolerance_round_value)) {
                    if (
                        qSVG.btwn(binder.start.x, limits[0].x, limits[1].x, false, MapConstants.tolerance_round_value)
                    ) {
                        //take start wall point as reference
                        const measure = qSVG.measure(limits[1], binder.start);
                        objTarget.x! -= measure;
                    } else if (
                        qSVG.btwn(binder.end.x, limits[0].x, limits[1].x, false, MapConstants.tolerance_round_value)
                    ) {
                        //take end wall point as reference
                        const measure = qSVG.measure(limits[1], binder.end);
                        objTarget.x! += measure;
                    }
                }

                if (!qSVG.btwn(limits[0].y, binder.start.y, binder.end.y, false, MapConstants.tolerance_round_value)) {
                    if (
                        qSVG.btwn(binder.start.y, limits[0].y, limits[1].y, false, MapConstants.tolerance_round_value)
                    ) {
                        //take start wall point as reference
                        const measure = qSVG.measure(limits[0], binder.start);
                        objTarget.y! += measure;
                    } else if (
                        qSVG.btwn(binder.end.y, limits[0].y, limits[1].y, false, MapConstants.tolerance_round_value)
                    ) {
                        //take end wall point as reference
                        const measure = qSVG.measure(limits[0], binder.end);
                        objTarget.y! += measure;
                    }
                }

                if (!qSVG.btwn(limits[1].y, binder.start.y, binder.end.y, false, MapConstants.tolerance_round_value)) {
                    if (
                        qSVG.btwn(binder.start.y, limits[0].y, limits[1].y, false, MapConstants.tolerance_round_value)
                    ) {
                        //take start wall point as reference
                        const measure = qSVG.measure(limits[1], binder.start);
                        objTarget.y! -= measure;
                    } else if (
                        qSVG.btwn(binder.end.y, limits[0].y, limits[1].y, false, MapConstants.tolerance_round_value)
                    ) {
                        //take end wall point as reference
                        const measure = qSVG.measure(limits[1], binder.end);
                        objTarget.y! += measure;
                    }
                }

                objTarget.limit = limits;

                //* FIX NEW COORD
                openings.forEach((opening) => {
                    if (opening.openingId === objTarget.openingId) {
                        opening.x = objTarget.x;
                        opening.y = objTarget.y;
                        opening.limit = objTarget.limit;
                    }
                });
            });

            const binderParent = WallUtils.getWallParent(binder, walls)!;
            const binderChild = WallUtils.getWallChild(binder, walls)!;
            const siblingWalls: Array<WallLine> = [
                {
                    start: intersection1,
                    end: intersection2,
                    angleRad: qSVG.getAngle(intersection1, intersection2).rad,
                    roomId: binder.roomId,
                },
                {
                    start: binderParent.start,
                    end: intersection1,
                    angleRad: qSVG.getAngle(binderParent.start, intersection1).rad,
                    roomId: binder.roomId,
                },
                {
                    start: intersection2,
                    end: binderChild.end,
                    angleRad: qSVG.getAngle(intersection2, binderChild.end).rad,
                    roomId: binder.roomId,
                },
            ];

            siblingWalls.forEach((siblingWall) => {
                const objWall = Editor.objFromWall(siblingWall, openings); // LIST OBJ ON EDGE
                for (let ob in objWall) {
                    const objTarget = objWall[ob];
                    const eq = Editor.createEquationFromWall(siblingWall);
                    const limits = PointUtils.limitObj(eq, objTarget.size!, objTarget as CoordPoint);
                    if (
                        !qSVG.btwn(limits[0].x, siblingWall.start.x, siblingWall.end.x) ||
                        !qSVG.btwn(limits[0].y, siblingWall.start.y, siblingWall.end.y) ||
                        !qSVG.btwn(limits[1].x, siblingWall.start.x, siblingWall.end.x) ||
                        !qSVG.btwn(limits[1].y, siblingWall.start.y, siblingWall.end.y)
                    ) {
                        WallTools.slideObjOnWall(siblingWall, openings);
                    }
                }
            });
        }

        return { walls, rooms, openings, wallMoveSuccess: true };
    }

    public static wallMoveMinSizeBlock(targetWall: Wall, roomWalls: Array<Wall>) {
        const targetWallIndex = roomWalls.findIndex((x) => x.wallId === targetWall.wallId);
        const parentWall = ObjectUtils.atCircular(roomWalls, targetWallIndex - 1);
        const childWall = ObjectUtils.atCircular(roomWalls, targetWallIndex + 1);

        if (
            (parentWall &&
                WallUtils.wallSize(parentWall) + MapConstants.tolerance_round_value < (parentWall.minimumSize ?? 0)) ||
            (childWall &&
                WallUtils.wallSize(childWall) + MapConstants.tolerance_round_value < (childWall.minimumSize ?? 0))
        ) {
            return true;
        }
        return false;
    }

    public static wallMinSizeCoordinates(targetwall: Wall, anchorSide: 'start' | 'end') {
        if (targetwall.minimumSize) {
            // attention entraine de l'imprecision
            const vector = {
                x: Math.cos(targetwall.angleRad) * targetwall.minimumSize,
                y: Math.sin(targetwall.angleRad) * targetwall.minimumSize,
            };
            if (anchorSide === 'start') {
                const minimumSizeEndPoint = {
                    x: targetwall.start.x + vector.x,
                    y: targetwall.start.y + vector.y,
                };

                return { start: targetwall.start, end: minimumSizeEndPoint };
            } else if (anchorSide === 'end') {
                const minimumSizeStartPoint = {
                    x: targetwall.end.x - vector.x,
                    y: targetwall.end.y - vector.y,
                };

                return { start: minimumSizeStartPoint, end: targetwall.end };
            }
        }
        return undefined;
    }

    public static direction(a: CoordPoint, b: CoordPoint, c: CoordPoint) {
        const val = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
        if (val === 0) {
            return 0;
        } else if (val < 0) {
            return 2;
        }
        return 1;
    }

    public static isIntersect(l1: LineCoordPoint, l2: LineCoordPoint) {
        let dir1 = this.direction(l1.p1, l1.p2, l2.p1);
        let dir2 = this.direction(l1.p1, l1.p2, l2.p2);
        let dir3 = this.direction(l2.p1, l2.p2, l1.p1);
        let dir4 = this.direction(l2.p1, l2.p2, l1.p2);

        if (dir1 !== dir2 && dir3 !== dir4) {
            return true;
        }
        if (dir1 === 0 && this.onLine(l1, l2.p1)) {
            return true;
        }
        if (dir2 === 0 && this.onLine(l1, l2.p2)) {
            return true;
        }
        if (dir3 === 0 && this.onLine(l2, l1.p1)) {
            return true;
        }
        if (dir4 === 0 && this.onLine(l2, l1.p2)) {
            return true;
        }
        return false;
    }

    public static onLine(l1: LineCoordPoint, p: CoordPoint) {
        return (
            p.x <= Math.max(l1.p1.x, l1.p2.x) &&
            p.x <= Math.min(l1.p1.x, l1.p2.x) &&
            p.y <= Math.max(l1.p1.y, l1.p2.y) &&
            p.y <= Math.min(l1.p1.y, l1.p2.y)
        );
    }

    public static intersect(l1: LineCoordPoint, l2: LineCoordPoint) {
        // Check if none of the lines are of length 0
        if ((l1.p1.x === l1.p2.x && l1.p1.y === l1.p2.y) || (l2.p1.x === l2.p2.x && l2.p1.y === l2.p2.y)) {
            return false;
        }

        const denominator = (l2.p2.y - l2.p1.y) * (l1.p2.x - l1.p1.x) - (l2.p2.x - l2.p1.x) * (l1.p2.y - l1.p1.y);

        // Lines are parallel
        if (denominator === 0) {
            return false;
        }

        let ua = ((l2.p2.x - l2.p1.x) * (l1.p1.y - l2.p1.y) - (l2.p2.y - l2.p1.y) * (l1.p1.x - l2.p1.x)) / denominator;
        let ub = ((l1.p2.x - l1.p1.x) * (l1.p1.y - l2.p1.y) - (l1.p2.y - l1.p1.y) * (l1.p1.x - l2.p1.x)) / denominator;

        // is the intersection along the segments
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
            return false;
        }

        // Return a object with the x and y coordinates of the intersection
        let x = l1.p1.x + ua * (l1.p2.x - l1.p1.x);
        let y = l1.p1.y + ua * (l1.p2.y - l1.p1.y);

        return { x, y };
    }
}
